import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import { api, markRecentLogin } from "@/lib/api";

interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  /** Only present when true (platform super-admin). */
  isSuperAdmin?: boolean;
  /** Only present when true (staff login). */
  isStaff?: boolean;
  permissions: string[];
  memberships: Array<{
    tenantId: string;
    tenantSlug?: string;
    role: string;
    roleName?: string;
  }>;
  staffId?: string;
  tenantId?: string;
  tenantSlug?: string;
  roleId?: string;
  roleName?: string;
  branchId?: string;
  branchName?: string;
  departmentId?: string;
  departmentName?: string;
  jobTitleId?: string;
  jobTitleName?: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (
    email: string,
    password: string,
    tenantSlug?: string
  ) => Promise<{ redirectUrl?: string; authCode?: string }>;
  loginWithCode: (code: string) => Promise<boolean>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function normalizeUser(raw: unknown): User | null {
  if (!raw || typeof raw !== "object") return null;
  const data = raw as Record<string, unknown>;
  if (typeof data.id !== "string" || typeof data.email !== "string") return null;

  const memberships = Array.isArray(data.memberships)
    ? (data.memberships as User["memberships"])
    : [];

  return {
    id: data.id,
    email: data.email,
    firstName: typeof data.firstName === "string" ? data.firstName : undefined,
    lastName: typeof data.lastName === "string" ? data.lastName : undefined,
    isSuperAdmin: data.isSuperAdmin === true ? true : undefined,
    isStaff: data.isStaff === true ? true : undefined,
    permissions: Array.isArray(data.permissions) ? (data.permissions as string[]) : [],
    memberships,
    staffId: typeof data.staffId === "string" ? data.staffId : undefined,
    tenantId: typeof data.tenantId === "string" ? data.tenantId : undefined,
    tenantSlug: typeof data.tenantSlug === "string" ? data.tenantSlug : undefined,
    roleId: typeof data.roleId === "string" ? data.roleId : undefined,
    roleName: typeof data.roleName === "string" ? data.roleName : undefined,
    branchId: typeof data.branchId === "string" ? data.branchId : undefined,
    branchName: typeof data.branchName === "string" ? data.branchName : undefined,
    departmentId: typeof data.departmentId === "string" ? data.departmentId : undefined,
    departmentName: typeof data.departmentName === "string" ? data.departmentName : undefined,
    jobTitleId: typeof data.jobTitleId === "string" ? data.jobTitleId : undefined,
    jobTitleName: typeof data.jobTitleName === "string" ? data.jobTitleName : undefined,
  };
}

function cacheTenantSlug(user: User): void {
  const slug = user.tenantSlug || user.memberships[0]?.tenantSlug;
  if (slug) {
    sessionStorage.setItem("tenant_slug", slug);
  }
}

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  /**
   * Fetch the current user profile from the server.
   * The access token is sent automatically as an httpOnly cookie.
   */
  const verifySession = useCallback(async (signal?: AbortSignal) => {
    try {
      const res = await api.get("/auth/me", { signal });
      const userData = normalizeUser(res.data);
      if (!userData) return false;

      setUser(userData);
      cacheTenantSlug(userData);

      return true;
    } catch {
      return false;
    }
  }, []);

  // On mount: try to load the session from the httpOnly cookie
  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;

    const init = async () => {
      // The httpOnly access_token cookie is sent automatically.
      // Just call /auth/me — if the cookie is valid we get the user.
      const ok = await verifySession(controller.signal);

      if (!cancelled && !ok) {
        // Access token cookie expired — try a silent refresh
        try {
          await api.post("/auth/refresh", {});
          await verifySession(controller.signal);
        } catch {
          // No valid session — user needs to log in
        }
      }

      if (!cancelled) setIsLoading(false);
    };

    init();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [verifySession]);

  // Save current location on actual page refresh for post-login restoration
  useEffect(() => {
    const saveLocation = () => {
      const currentPath = window.location.pathname;
      const publicRoutes = [
        "/login",
        "/",
        "/onboard",
        "/register",
        "/verify-email",
        "/reset-password",
        "/staff/accept-invite",
      ];
      if (!publicRoutes.includes(currentPath)) {
        sessionStorage.setItem("lastLocation", currentPath + window.location.search);
      }
    };
    window.addEventListener("beforeunload", saveLocation);
    return () => window.removeEventListener("beforeunload", saveLocation);
  }, []);

  const login = async (email: string, password: string, tenantSlug?: string) => {
    const response = await api.post("/auth/login", { email, password, tenantSlug });
    const { redirectUrl, authCode } = response.data;

    sessionStorage.removeItem("_auth_redirect_ts");
    markRecentLogin();

    let ok = await verifySession();
    if (!ok) {
      try {
        await api.post("/auth/refresh", {});
        ok = await verifySession();
      } catch {
        // Session not established
      }
    }

    if (!ok) {
      const needsCrossDomainRedirect = Boolean(
        redirectUrl && redirectUrl.startsWith("http") && authCode
      );
      if (!needsCrossDomainRedirect) {
        throw new Error(
          "Login succeeded but your session could not be established. Try again, or clear cookies for this site."
        );
      }
    }

    return { redirectUrl, authCode };
  };

  const loginWithCode = useCallback(
    async (code: string): Promise<boolean> => {
      try {
        await api.post("/auth/exchange-code", { code });
        markRecentLogin();
        // Cookies are set by the server — verify session
        const ok = await verifySession();
        if (!ok) setUser(null);
        return ok;
      } catch {
        return false;
      }
    },
    [verifySession]
  );

  const logout = async () => {
    try {
      await api.post("/auth/logout", {});
    } catch {
      // Server-side logout is best-effort
    }
    setUser(null);
    sessionStorage.removeItem("tenant_slug");
  };

  const isAuthenticated = !!user;

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        isLoading,
        login,
        loginWithCode,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
